import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { X, Loader2, Info } from "lucide-react";
import { useEscapeKey } from "@/hooks/useEscapeKey";

const inputCls = "mt-1 w-full rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-3 py-2 text-sm text-[#1C1C1E] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#FFC817]/50";
const labelCls = "text-[10px] uppercase tracking-wider text-[#A8A29E]";

// Operational Control Upgrade Part 3.1 (2026-02): same server-side field tiering as
// EditFreelancerProfileModal — Approved/Portal Access are Founder/Partner-only even at create
// time (a PM-created record always lands Approved=No/Portal Access=No, see
// _FOUNDER_ONLY_CREATE_FIELDS in modules/build.py). This UI split is a convenience only; the
// real boundary is server-side and rejects a forbidden field with 403 regardless.
const REQUIRED_FIELDS = [
  { key: "name", label: "Full Name", kind: "text" },
  { key: "registered_gmail", label: "Registered Gmail", kind: "text" },
];
const OPERATIONAL_FIELDS = [
  { key: "phone", label: "Phone", kind: "text" },
  { key: "city", label: "City / Location", kind: "text" },
  { key: "country", label: "Country", kind: "text" },
  { key: "primary_role", label: "Primary Role", kind: "select", optionsKey: "primary_role" },
  { key: "secondary_roles", label: "Secondary Roles", kind: "multiselect", optionsKey: "secondary_roles" },
  { key: "skills", label: "Skills", kind: "text" },
  { key: "bio", label: "Bio", kind: "textarea" },
  { key: "website", label: "Website", kind: "text" },
  { key: "instagram", label: "Instagram", kind: "text" },
  { key: "youtube", label: "YouTube", kind: "text" },
  { key: "vimeo", label: "Vimeo", kind: "text" },
  { key: "behance", label: "Behance", kind: "text" },
  { key: "camera_owned", label: "Camera Owned", kind: "text" },
  { key: "lenses", label: "Lenses", kind: "text" },
  { key: "lighting", label: "Lighting", kind: "text" },
  { key: "audio", label: "Audio", kind: "text" },
  { key: "drone", label: "Drone", kind: "text" },
  { key: "editing_setup", label: "Editing Setup", kind: "text" },
  { key: "nda_signed", label: "NDA Signed", kind: "checkbox" },
  { key: "internal_notes", label: "Internal Notes", kind: "textarea" },
];
const FOUNDER_ONLY_FIELDS = [
  { key: "approved", label: "Approved", kind: "checkbox" },
  { key: "portal_access", label: "Portal Access", kind: "checkbox" },
];

function Field({ f, value, options, onChange }) {
  const testId = `create-freelancer-${f.key}`;
  if (f.kind === "select") {
    return (
      <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">—</option>
        {(options?.[f.optionsKey] || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.kind === "multiselect") {
    const selected = value || [];
    return (
      <div className="mt-1 flex flex-wrap gap-2" data-testid={testId}>
        {(options?.[f.optionsKey] || []).map((o) => (
          <label key={o} className="inline-flex items-center gap-1.5 rounded-full border border-[#E7E5E4] px-2.5 py-1 text-xs cursor-pointer">
            <input
              type="checkbox" checked={selected.includes(o)} data-testid={`${testId}-${o}`}
              onChange={(e) => onChange(e.target.checked ? [...selected, o] : selected.filter((s) => s !== o))}
            />
            {o}
          </label>
        ))}
      </div>
    );
  }
  if (f.kind === "checkbox") {
    return <input type="checkbox" data-testid={testId} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1.5 h-4 w-4" />;
  }
  if (f.kind === "textarea") {
    return <textarea data-testid={testId} value={value || ""} rows={3} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  }
  return <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
}

export function CreateFreelancerModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const canSetSensitive = user?.role === "founder" || user?.role === "partner";
  const fields = [...REQUIRED_FIELDS, ...OPERATIONAL_FIELDS, ...(canSetSensitive ? FOUNDER_ONLY_FIELDS : [])];

  const [options, setOptions] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  useEscapeKey(onClose);

  useEffect(() => {
    api.get("/v1/build/freelancers/field-options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const setField = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const save = async () => {
    if (!values.name?.trim() || !values.registered_gmail?.trim()) {
      toast.error("Full Name and Registered Gmail are required");
      return;
    }
    setSaving(true);
    try {
      const body = {};
      fields.forEach((f) => { if (values[f.key] !== undefined && values[f.key] !== "") body[f.key] = values[f.key]; });
      const r = await api.post("/v1/build/freelancers", body);
      toast.success(`${r.data.freelancer.name} added to the Freelancer Directory`);
      onCreated?.(r.data.freelancer);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "registered_gmail_already_exists") {
        toast.error(detail.existing_freelancer_name
          ? `This Gmail is already registered to freelancer ${detail.existing_freelancer_name}`
          : "This Gmail is already registered to another freelancer");
      }
      else if (detail?.code === "registered_gmail_collides_with_staff_account") toast.error("This Gmail belongs to an existing NJM staff account");
      else if (detail?.code === "forbidden_field") toast.error(`Not allowed to set: ${detail.fields.join(", ")}`);
      else if (detail?.code === "missing_required_field") toast.error(`Missing: ${detail.fields.join(", ")}`);
      // permission_denied is permission_engine.check()'s generic 403 shape ({code, scope, role})
      // — was previously falling through to the catch-all below and showing the raw code
      // verbatim ("permission_denied") instead of a human-readable message.
      else if (detail?.code === "permission_denied") toast.error("You don't have permission to create freelancers: this requires Founder, Partner, or PM.");
      else toast.error(detail?.error || detail?.code || "Couldn't create freelancer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="create-freelancer-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="create-freelancer-modal-backdrop" />
      <div className="relative w-full max-w-md h-full bg-[#FFFFFF] border-l border-[#E7E5E4] overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between border-b border-[#E7E5E4] px-5 py-4 sticky top-0 bg-[#FFFFFF] z-10">
          <h2 className="font-editorial text-xl tracking-tight">New Freelancer</h2>
          <button data-testid="create-freelancer-modal-close" onClick={onClose} className="text-[#A8A29E] hover:text-[#1C1C1E]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-md bg-[#FFC817]/10 border border-[#FFC817]/30 px-3 py-2.5 text-[11px] text-[#57534E] flex items-start gap-2" data-testid="create-freelancer-google-testing-note">
            <Info className="h-3.5 w-3.5 text-[#B45309] shrink-0 mt-0.5" />
            While the NJM Build Google OAuth app is in Testing mode, this Gmail must ALSO be added
            as a Google test user (or the app published) before login will actually work. Creating
            this record plus Approved + Portal Access alone does not grant login.
          </div>

          {REQUIRED_FIELDS.map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label} <span className="text-red-500">*</span></label>
              <Field f={f} value={values[f.key]} options={options} onChange={(v) => setField(f.key, v)} />
            </div>
          ))}

          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#A8A29E] pt-2 border-t border-[#E7E5E4]">Operational</div>
          {OPERATIONAL_FIELDS.map((f) => (
            <div key={f.key} className={f.kind === "checkbox" ? "flex items-center justify-between" : ""}>
              <label className={labelCls}>{f.label}</label>
              <Field f={f} value={values[f.key]} options={options} onChange={(v) => setField(f.key, v)} />
            </div>
          ))}

          {canSetSensitive ? (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[#A8A29E] pt-2 border-t border-[#E7E5E4]">
                Founder / Partner Only
              </div>
              {FOUNDER_ONLY_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center justify-between">
                  <label className={labelCls}>{f.label}</label>
                  <Field f={f} value={values[f.key]} options={options} onChange={(v) => setField(f.key, v)} />
                </div>
              ))}
            </>
          ) : (
            <div className="text-[11px] text-[#A8A29E] pt-2 border-t border-[#E7E5E4]" data-testid="create-freelancer-pm-notice">
              Approved and Portal Access default to No. Only Founder/Partner can grant login access, from the freelancer's profile page.
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-[#FFFFFF] border-t border-[#E7E5E4] px-5 py-4 flex justify-end gap-2">
          <button data-testid="create-freelancer-modal-cancel" onClick={onClose} className="rounded-md border border-[#E7E5E4] px-4 py-2 text-sm text-[#57534E] hover:bg-black/[0.03]">
            Cancel
          </button>
          <button
            data-testid="create-freelancer-modal-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#FFC817] px-4 py-2 text-sm font-medium text-[#1C1C1E] hover:bg-[#FFC817]/90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create Freelancer
          </button>
        </div>
      </div>
    </div>
  );
}
